/**
 * ANJA quick shop: a single slide-in drawer that shops a whole look without leaving the page.
 *
 * Any element with [data-anja-quickshop-open][data-products="handle,handle"] opens it. Products are
 * fetched on demand from the locale-aware Ajax product endpoint (/products/<handle>.js), so pages with
 * 50 looks stay light. The drawer builds the same <anja-look>/<anja-look-item> markup the rest of the
 * theme uses, so option resolution, totals and the single multi-item cart add (with cart re-fetch and
 * honest partial/failed reporting) come from anja-look.js unchanged.
 */
import { formatMoney } from '@theme/money-formatting';

const cache = new Map();

function config() {
  const el = document.querySelector('script[data-anja-quickshop-config]');
  try {
    return el ? JSON.parse(el.textContent || '{}') : null;
  } catch {
    return null;
  }
}

/** @param {string} value */
function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

/**
 * @param {string} root
 * @param {string} handle
 */
function fetchProduct(root, handle) {
  const url = `${root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}.js`;
  if (!cache.has(url)) {
    cache.set(
      url,
      fetch(url, { headers: { Accept: 'application/json' } }).then((response) => {
        if (!response.ok) throw new Error(`Product ${handle}: ${response.status}`);
        return response.json();
      })
    );
  }
  return /** @type {Promise<any>} */ (cache.get(url));
}

class AnjaQuickshop {
  /** @type {HTMLDialogElement} */
  dialog;
  /** @type {HTMLElement | null} */
  opener = null;

  constructor(cfg) {
    this.cfg = cfg;
    const l = cfg.labels;
    const dialog = document.createElement('dialog');
    dialog.className = 'anja-quickshop';
    dialog.setAttribute('aria-labelledby', 'AnjaQuickshopTitle');
    dialog.innerHTML = `
      <div class="anja-quickshop__panel">
        <header class="anja-quickshop__header">
          <div>
            <p class="anja-eyebrow">${escapeHtml(l.title)}</p>
            <h2 class="anja-quickshop__title" id="AnjaQuickshopTitle"></h2>
          </div>
          <button type="button" class="anja-quickshop__close" data-anja-quickshop-close aria-label="${escapeHtml(l.close)}">
            <svg class="anja-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="m5 5 14 14M5 19 19 5"/></svg>
          </button>
        </header>
        <div class="anja-quickshop__body" data-anja-quickshop-body></div>
      </div>`;
    document.body.append(dialog);
    this.dialog = dialog;
    this.body = /** @type {HTMLElement} */ (dialog.querySelector('[data-anja-quickshop-body]'));
    this.title = /** @type {HTMLElement} */ (dialog.querySelector('.anja-quickshop__title'));

    dialog.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      if (target === dialog || target.closest('[data-anja-quickshop-close]')) this.close();
    });
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('anja-quickshop-open');
      if (this.opener?.isConnected) this.opener.focus();
    });
  }

  close() {
    this.dialog.close();
  }

  /** @param {HTMLElement} opener */
  async open(opener) {
    this.opener = opener;
    const handles = (opener.dataset.products || '').split(',').filter(Boolean);
    const focus = opener.dataset.focus || '';
    this.title.textContent = opener.dataset.lookTitle || '';
    this.body.innerHTML = `<p class="anja-quickshop__status" role="status">${escapeHtml(this.cfg.labels.loading)}</p>`;
    document.documentElement.classList.add('anja-quickshop-open');
    this.dialog.showModal();

    const results = await Promise.allSettled(handles.map((h) => fetchProduct(this.cfg.root, h)));
    const products = results.filter((r) => r.status === 'fulfilled').map((r) => /** @type {any} */ (r).value);
    if (!products.length) {
      this.body.innerHTML = `<p class="anja-quickshop__status" role="alert">${escapeHtml(this.cfg.labels.loadError)}</p>`;
      return;
    }
    this.render(products, focus);
  }

  /** @param {number} cents */
  money(cents) {
    const { currency, moneyFormat, locale } = this.cfg;
    if (moneyFormat) return formatMoney(cents, moneyFormat, currency);
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    } catch {
      return String(cents / 100);
    }
  }

  /**
   * @param {any[]} products
   * @param {string} focus
   */
  render(products, focus) {
    const l = this.cfg.labels;
    const items = products
      .map((p, index) => {
        const supported = !p.requires_selling_plan && p.variants.length > 0;
        const single = p.variants.length === 1 && (p.options.length === 0 || p.variants[0].title === 'Default Title');
        const image = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=240` : '';
        const varies = p.price_min !== p.price_max;
        const price = varies ? l.messages.fromPrice.replace('[price]', this.money(p.price_min)) : this.money(p.price_min);
        const variants = p.variants.map((v) => ({
          id: v.id,
          options: v.options,
          price: v.price,
          available: v.available,
          image: v.featured_image ? `${v.featured_image.src}${v.featured_image.src.includes('?') ? '&' : '?'}width=240` : null,
        }));
        const fields = single
          ? ''
          : p.options
              .map((option, i) => {
                const name = typeof option === 'string' ? option : option.name;
                const values = typeof option === 'string' ? [...new Set(p.variants.map((v) => v.options[i]))] : option.values;
                const id = `AnjaQs-${p.id}-${i}`;
                if (values.length === 1) {
                  return `<p class="anja-look-item__fixed-option">${escapeHtml(name)}: ${escapeHtml(values[0])}</p>
                    <input type="hidden" data-anja-option="${i}" value="${escapeHtml(values[0])}">`;
                }
                return `<div class="anja-look-item__field">
                    <label for="${id}">${escapeHtml(name)}</label>
                    <select id="${id}" data-anja-option="${i}" required>
                      <option value="">${escapeHtml(l.chooseOption.replace('[option]', name))}</option>
                      ${values.map((v) => `<option value="${escapeHtml(v)}" data-label="${escapeHtml(v)}">${escapeHtml(v)}</option>`).join('')}
                    </select>
                  </div>`;
              })
              .join('');
        const soldOut = !p.available ? `<p class="anja-look-item__note anja-look-item__note--error">${escapeHtml(l.soldOut)}</p>` : '';
        return `<anja-look-item class="anja-look-item${p.handle === focus ? ' is-focus' : ''}" data-required="true"
            data-supported="${supported}" data-title="${escapeHtml(p.title)}" data-handle="${escapeHtml(p.handle)}" style="--i:${index}">
            <a class="anja-look-item__media" href="${escapeHtml(p.url)}" tabindex="-1" aria-hidden="true">
              ${image ? `<img src="${escapeHtml(image)}" alt="" width="120" height="160" loading="lazy" data-anja-item-image>` : ''}
            </a>
            <div class="anja-look-item__info">
              <h3 class="anja-look-item__title"><a href="${escapeHtml(p.url)}">${escapeHtml(p.title)}</a></h3>
              <p class="anja-look-item__meta"><span data-anja-item-price>${escapeHtml(price)}</span></p>
              ${soldOut}${p.available ? fields : ''}
              <script type="application/json" data-anja-variants>${JSON.stringify(variants).replace(/</g, '\\u003c')}</script>
            </div>
          </anja-look-item>`;
      })
      .join('');

    this.body.innerHTML = `
      <anja-look class="anja-look anja-look--drawer" data-currency="${escapeHtml(this.cfg.currency)}"
        data-money-format="${escapeHtml(this.cfg.moneyFormat || '')}" data-locale="${escapeHtml(this.cfg.locale)}">
        <div class="anja-look__shop">
          <div class="anja-look__items">${items}</div>
          <div class="anja-look__total"><span>${escapeHtml(l.total)}</span><strong data-anja-total aria-live="off"></strong></div>
          <button type="button" class="anja-button anja-button--brand anja-button--block anja-look__add" data-anja-add disabled
            data-label="${escapeHtml(l.add)}" data-label-busy="${escapeHtml(l.adding)}">${escapeHtml(l.add)}</button>
          <p class="anja-look__guidance" data-anja-guidance role="status" aria-live="polite"></p>
          <p class="anja-look__note">${escapeHtml(l.cartNote)}</p>
          <template data-anja-messages>${JSON.stringify(l.messages)}</template>
        </div>
      </anja-look>`;

    const focused = this.body.querySelector('anja-look-item.is-focus');
    (focused?.querySelector('select') || this.body.querySelector('select'))?.focus({ preventScroll: false });
    focused?.scrollIntoView({ block: 'nearest' });
  }
}

/** @type {AnjaQuickshop | null} */
let instance = null;

document.addEventListener('click', (event) => {
  const opener = /** @type {HTMLElement} */ (event.target).closest('[data-anja-quickshop-open]');
  if (!(opener instanceof HTMLElement)) return;
  const cfg = config();
  if (!cfg || typeof HTMLDialogElement !== 'function') return;
  event.preventDefault();
  instance ??= new AnjaQuickshop(cfg);
  instance.open(opener);
});

// Close the drawer once the look has been added; Horizon's cart drawer then takes over.
document.addEventListener('anja-look:added', () => instance?.close());
